import * as THREE from 'three';

export function correctAxisData(array: Float32Array): Float32Array {
  var result = [];
  for (var i = 0; i + 2 < array.length; i += 3) {
    result.push(array[i + 1], array[i + 2], array[i]);
  }

  return new Float32Array(result);
}

function createBufferGeometry(data: any): THREE.BufferGeometry {
  const baseVertices = correctAxisData(new Float32Array(data.vertices));
  const baseNormals = correctAxisData(new Float32Array(data.normals));

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(baseVertices, 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(baseNormals, 3));

  const baseUV = data.uv_coord ? new Float32Array(data.uv_coord) : null;
  if (baseUV) geometry.setAttribute('uv', new THREE.BufferAttribute(baseUV, 2));

  return geometry;
}

function centerGeometry(geometry: THREE.BufferGeometry) {
  geometry.computeBoundingBox();
  let center = new THREE.Vector3();
  geometry.boundingBox?.getCenter(center);
  geometry.center();

  return center;
}

function sizeOfGeometry(geometry: THREE.BufferGeometry) {
  geometry.computeBoundingBox();
  let size = new THREE.Vector3();
  geometry.boundingBox?.getSize(size);

  return size;
}

export function get3DMeshDataAndPosition(geometry: THREE.BufferGeometry) {
  const position = centerGeometry(geometry);

  return {
    vertices: geometry.getAttribute('position').array,
    normals: geometry.getAttribute('normal').array,
    uv_coord: geometry.getAttribute('uv')
      ? geometry.getAttribute('uv').array
      : null,
    position: position,
    size: sizeOfGeometry(geometry),
  };
}

export function compute3DMeshDataAndPosition(data: any) {
  const geometry = createBufferGeometry(data);

  return get3DMeshDataAndPosition(geometry);
}

function getTextureName(texturePath: string) {
  const textureInfosPath = texturePath.split('/');
  const lastTexturePathInfo = textureInfosPath[
    textureInfosPath.length - 1
  ].split('.');
  return `${lastTexturePathInfo[0]}.${
    lastTexturePathInfo[lastTexturePathInfo.length - 1]
  }`;
}

export function loadTexture(texturePath: string) {
  return new THREE.TextureLoader().load(texturePath, function(texture) {
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.minFilter = THREE.LinearMipMapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.name = getTextureName(texturePath);
  });
}

export function replaceGeometryAttributesOnGeometry(
  geometry: THREE.BufferGeometry,
  data: any,
) {
  geometry.setAttribute(
    'position',
    new THREE.BufferAttribute(data.vertices, 3),
  );
  geometry.setAttribute('normal', new THREE.BufferAttribute(data.normals, 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(data.uv_coord, 2));
}

export function computeFov(
  objectsTotalWidth: number,
  objectsTotalHeight: number,
  canvasAspect: number,
  distanceCameraZ: number,
) {
  // Compute vertival and horizontal FOV based on objects dimension and distance to camera on Z axis
  const vfov = 2 * Math.atan(objectsTotalHeight / (2 * distanceCameraZ));
  const hfov = 2 * Math.atan(objectsTotalWidth / (2 * distanceCameraZ));

  // Because we need only vertical FOV, convert horizontal FOV to vertical FOV relative to scene aspect ratio
  const vfovhbased = 2 * Math.atan(Math.tan(hfov / 2) / canvasAspect);

  // Convert the two FOV in radians because radians is necessary for the THREE camera FOV parameter
  const vfovRadians = (vfov * 180) / Math.PI;
  const vfovhbasedRadians = (vfovhbased * 180) / Math.PI;

  // Keep the biggest vertical FOV to ensure to have object entirely visible in width and in height
  return Math.max(vfovRadians, vfovhbasedRadians);
}

export function makeMeshWithData(data: {
  vertices: [number];
  normals: [number];
  uv_coord?: [number];
}) {
  const vertices = new Float32Array(data.vertices);
  const normals = new Float32Array(data.normals);
  const uv_coord = data.uv_coord ? new Float32Array(data.uv_coord) : null;

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.BufferAttribute(correctAxisData(vertices), 3),
  );
  geometry.setAttribute(
    'normal',
    new THREE.BufferAttribute(correctAxisData(normals), 3),
  );
  if (uv_coord)
    geometry.setAttribute('uv', new THREE.BufferAttribute(uv_coord, 2));

  const material = new THREE.MeshPhongMaterial({
    color: 0xffffff,
    opacity: 1,
    transparent: true,
    shininess: 4.92458,
    side: THREE.FrontSide,
    specular: 0x919191,
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.receiveShadow = true;
  mesh.castShadow = true;

  return mesh;
}
